import React, { useState } from 'react';

/**
 * Hard anti-scrape email reveal.
 * - The cleartext address NEVER appears in the rendered DOM until the user clicks.
 * - The address is base64-encoded and decoded only on interaction.
 * - Bots that parse innerText / innerHTML / mailto links find nothing.
 *
 * Usage:
 *   <ObfuscatedEmail email="planning@puzzlevideo.fr" />
 *
 * Props:
 *   email   - the cleartext address (will be base64 encoded at render)
 *   className - optional Tailwind classes for the link / button
 *   testId    - data-testid base ("xxx-button" before reveal, "xxx-link" after)
 */
const ObfuscatedEmail = ({ email, className = '', testId = 'obfuscated-email', revealLabel = 'Afficher l\u2019email' }) => {
  const [revealed, setRevealed] = useState(false);
  if (!email) return <span className="text-muted-foreground italic text-sm">…</span>;

  // Encode once per render — string is never reassembled in source.
  const encoded = typeof window !== 'undefined' && window.btoa
    ? window.btoa(unescape(encodeURIComponent(email)))
    : Buffer.from(email, 'utf-8').toString('base64');

  if (revealed) {
    // Decode at click time only.
    const decoded = decodeURIComponent(escape(window.atob(encoded)));
    return (
      <a href={`mailto:${decoded}`} className={className} data-testid={`${testId}-link`}>
        {decoded}
      </a>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setRevealed(true)}
      className={className + ' cursor-pointer text-left'}
      data-testid={`${testId}-button`}
      aria-label={revealLabel}
    >
      <span aria-hidden="true">{revealLabel}</span>
    </button>
  );
};

export default ObfuscatedEmail;
